import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, AlertCircle, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";

// Polls /api/marketplace/status/{session_id} until paid/expired or 10 attempts.
export default function PaymentSuccess() {
    const { t } = useTranslation();
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [state, setState] = useState({ status: "loading" });

    useEffect(() => {
        if (!sessionId) {
            setState({ status: "failed" });
            return;
        }
        let attempts = 0;
        let cancelled = false;

        const poll = async () => {
            if (cancelled) return;
            attempts += 1;
            try {
                const { data } = await api.get(`/marketplace/status/${sessionId}`);
                if (data.payment_status === "paid") {
                    setState({ status: "paid", amount: data.amount, currency: data.currency });
                    return;
                }
                if (data.payment_status === "expired" || data.payment_status === "failed") {
                    setState({ status: "failed" });
                    return;
                }
            } catch {
                /* swallow & retry */
            }
            if (attempts >= 10) {
                setState({ status: "failed" });
                return;
            }
            setTimeout(poll, 2000);
        };
        poll();
        return () => { cancelled = true; };
    }, [sessionId]);

    return (
        <main className="min-h-[calc(100vh-73px)] flex flex-col items-center justify-center px-6 text-center">
            {state.status === "loading" && (
                <>
                    <Loader2 size={32} className="text-[var(--accent)] animate-spin mb-4" />
                    <p className="font-display text-2xl text-[var(--ink)] italic"
                       style={{ fontVariationSettings: '"wght" 500, "wdth" 90, "opsz" 32, "slnt" -10' }}>
                        {t("marketplace.payment_processing")}
                    </p>
                </>
            )}
            {state.status === "paid" && (
                <>
                    <Check size={36} className="text-[var(--accent)] mb-4" data-testid="payment-success-icon" />
                    <h1 className="font-display text-4xl md:text-5xl text-[var(--ink)] mb-4"
                        style={{ fontVariationSettings: '"wght" 600, "wdth" 85, "opsz" 96' }}>
                        {t("marketplace.payment_success_title")}
                    </h1>
                    <p className="text-[var(--ink-muted)] max-w-lg mb-8">
                        {t("marketplace.payment_success_lead")}
                    </p>
                    <Link to="/marketplace" className="bg-[var(--ink)] text-[var(--bg)] rounded-full hover:bg-[var(--accent)] transition-colors px-8 py-3 text-sm uppercase tracking-widest">
                        {t("marketplace.go_marketplace")}
                    </Link>
                </>
            )}
            {state.status === "failed" && (
                <>
                    <AlertCircle size={36} className="text-[var(--danger)] mb-4" />
                    <h1 className="font-display text-4xl md:text-5xl text-[var(--ink)] mb-4"
                        style={{ fontVariationSettings: '"wght" 600, "wdth" 85, "opsz" 96' }}>
                        {t("marketplace.payment_failed_title")}
                    </h1>
                    <p className="text-[var(--ink-muted)] max-w-lg mb-8">{t("marketplace.payment_failed_lead")}</p>
                    <Link to="/marketplace" className="border border-[var(--ink)] text-[var(--ink)] rounded-full hover:bg-[var(--bg-hover)] transition-colors px-8 py-3 text-sm uppercase tracking-widest">
                        {t("marketplace.go_marketplace")}
                    </Link>
                </>
            )}
        </main>
    );
}
